import {
	DispatcherV2Request,
	DispatcherV2Response,
	DispatcherV2SingleRequest,
	GroupData,
	RouteOrderByField,
	ItemsByGroup,
	ResponseItem,
	RunFilters
} from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';
import { chunk as _chunk, flatMap as _flatMap, flatten as _flatten } from 'lodash';
import { getActionableWaypoint } from '@bringg-frontend/global-stores';

import { fetchFieldsProvider } from '../../shared/utils/fetch-task-fields/fetch-fields-provider';
import RootStore from 'bringg-web/stores/root-store';
import { routesMonitoringRootStore } from '../stores/routes-monitoring-root-store';
import { getInnerGroups } from '../../shared/utils/grouping';
import { loaderPageCreator } from '../../shared/data/loader-page-creator';
import { monitoringFiltersProvider } from 'bringg-web/features/monitoring/shared/components/monitoring-filter-bar/utils/monitoring-filter-provider';
import { fetchPlannedRoutes } from '../../shared/utils/fetch-planned-routes';

const TASKS_CHUNK_SIZE = 150;

const splitTasksToChunks = (routes: ResponseItem[][], size = TASKS_CHUNK_SIZE): number[][] => {
	return _chunk(
		_flatMap(routes, r => r.map(item => item.id)),
		size
	);
};

const getTeamsFilter = (runFilters: RunFilters): number[] => {
	const { teamsStore } = getRoot<RootStore>().data;
	return runFilters.selectedTeams?.length ? runFilters.selectedTeams : teamsStore.all.map(({ id }) => id);
};

async function loadNextPage() {
	const { routesMonitoringStore } = routesMonitoringRootStore.getStore();

	const currentTableDefinition = routesMonitoringStore.currentTableDefinition;

	if (routesMonitoringStore.loading) {
		routesMonitoringStore.refreshAC.abort();
		routesMonitoringStore.refreshAC = new AbortController();
	}

	if (!Object.keys(routesMonitoringStore.currentTableDefinition?.itemsByGroup || {}).length) {
		routesMonitoringStore.setLoading(true);
	}

	const { groups, groupMode, sorting, entityType, itemsByGroup, filters } =
		routesMonitoringStore.currentTableDefinition;

	let response: DispatcherV2Response;
	try {
		const page = loaderPageCreator.create(groups, itemsByGroup, entityType);

		const runFilters = monitoringFiltersProvider.mapRunsFiltersToServer(filters.run);
		runFilters.selectedTeams = getTeamsFilter(runFilters);

		const routesRequestBase: DispatcherV2SingleRequest = {
			grouping: groupMode,
			sorting: sorting,
			get_all_tasks_for_task_group: true,
			filters: monitoringFiltersProvider.mapFiltersToServer(filters.task),
			runFilters,
			page,
			collapsed_groups: [],
			route_order_by_field: RouteOrderByField.StartTime,
			entity_type: entityType
		};

		const requests: DispatcherV2SingleRequest[] = [routesRequestBase];

		response = await load({ query_requests: requests }, { signal: routesMonitoringStore.disposeSignal });

		await Promise.all([
			fetchGroups(response.query_responses[0].groups),
			fetchTasks(response.query_responses[0].items_by_group, { signal: routesMonitoringStore.disposeSignal })
		]);

		if (currentTableDefinition === routesMonitoringStore.currentTableDefinition) {
			routesMonitoringStore.currentTableDefinition.toggleCollapseAll(true); // will make table definition to treat the view as collapsed view
			routesMonitoringStore.setResponse(response);
		}
	} catch (e) {
		routesMonitoringStore.currentTableDefinition.setHasMoreData(response);
		console.error('failed to fetch order explorer data ', e);
	} finally {
		routesMonitoringStore.setLoading(false);
	}
}

const load = async (
	request: DispatcherV2Request,
	options: {
		signal?: AbortSignal;
	} = {}
): Promise<DispatcherV2Response> => {
	if (!request.query_requests[0].runFilters?.selectedTeams?.length) {
		const teams = await getRoot<RootStore>().data.teamsStore.fetchAll();
		const teamIds = teams.map(({ id }) => id);

		request.query_requests[0].runFilters = {
			selectedTeams: teamIds,
			...request.query_requests[0].runFilters
		};
	}
	return await getRootEnv().dashboardSdk.sdk.v2.tasksExplorer.getAll(request, { signal: options.signal });
};

const fetchGroups = async (groups: GroupData[]) => {
	if (!groups.length) {
		return;
	}

	const routeGroups = getInnerGroups(groups);

	const ids = routeGroups.map(group => group.data.id);

	if (!ids.length) {
		return;
	}

	const { routesMonitoringStore } = routesMonitoringRootStore.getStore();
	const routes = await getRoot<RootStore>().data.runStore.loadMany({ ids }, undefined, {
		signal: routesMonitoringStore.disposeSignal
	});

	fetchPlannedRoutes(routes);
};

const fetchTasks = async (
	itemsByGroups: ItemsByGroup,
	options: {
		signal?: AbortSignal;
	} = {}
) => {
	const runsInChunks = splitTasksToChunks(Object.values(itemsByGroups));

	try {
		const response = await Promise.all(runsInChunks.map(async chunk => fetchTasksByIds(chunk, options)));

		const tasks = _flatten(response);
		const customerIdsToFetch = [];

		tasks.forEach(task => {
			if (task) {
				const waypoint = getActionableWaypoint(task);
				waypoint.customer_id && customerIdsToFetch.push(waypoint.customer_id);
			}
		});

		if (customerIdsToFetch.length) {
			await getRootEnv().dashboardSdk.sdk.v2.customers.loadMany(customerIdsToFetch, {
				useCache: true,
				chunkSize: TASKS_CHUNK_SIZE,
				signal: options.signal
			});
		}
	} catch (err) {
		console.error(`failed to fetch tasks, ${err} `, err);
	}
};
const fetchTasksByIds = async (ids: number[], options: { signal?: AbortSignal } = {}) => {
	return getRootEnv().dashboardSdk.sdk.v2.tasks.loadMany(ids, fetchFieldsProvider.get(), {
		useCache: true,
		...options
	});
};

export const routesMonitoringLoaderProvider = { loadNextPage };
